/**
 * The day's map — `docs/new-request.md` §10.6, Board 7.
 *
 * The route from where the person starts, through the day's stops in order
 * with the new place put where the visit sits, and back; the stops numbered,
 * the start as a house, the new place as the green pin. MapLibre on the
 * Google tiles base (`imagerySession`, the same one Property maps use) —
 * Google's terms want Places results shown on a Google map. **MapLibre
 * arrives by dynamic import**, so a sheet nobody opens a calendar in pays
 * nothing for it, and the map never pans, zooms or rotates under a hand: it
 * is a picture of a day, fitted to the day.
 *
 * With the imagery unavailable it draws the plain base — the route and the
 * markers are the information, the tiles the backdrop (§5.7 of the maps
 * contract, the same choice).
 */
import { useEffect, useRef, useState } from 'react'
import type { Map as MapLibreMap, Marker } from 'maplibre-gl'
import { metresPerPixel, milesTone, scaleBar, type CalendarDay, type DayPlan } from '@rowted/shared'
import { tokenColour } from '../../lib/token-colour.js'
import { imagerySession, type ImagerySession } from '../../lib/map-provider.js'
import { TONE_VAR } from './tones.js'

interface Point { lat: number; lng: number }

/** The route's corners, in order: start, the stops with the place at `gap`, and back. */
function routeOf(day: CalendarDay, gap: number | null): Point[] {
  const out: Point[] = []
  if (day.map.start) out.push(day.map.start)
  day.map.stops.forEach((stop, index) => {
    if (gap === index && day.map.place) out.push(day.map.place)
    if (stop.point) out.push(stop.point)
  })
  if (gap !== null && gap >= day.map.stops.length && day.map.place) out.push(day.map.place)
  if (day.map.start) out.push(day.map.start)
  return out
}

function markerElement(className: string, ...children: Element[]): HTMLElement {
  const element = document.createElement('div')
  element.className = className
  element.append(...children)
  return element
}

/**
 * An SVG element from camel-cased attributes, so no markup string sits in the
 * file. `viewBox` is the one attribute SVG spells in camel case itself — made
 * `view-box`, the glyph was drawn unscaled and the office showed one stroke.
 */
function svg(tag: string, attributes: Record<string, string>, ...children: Element[]): SVGElement {
  const node = document.createElementNS('http://www.w3.org/2000/svg', tag)
  for (const [name, value] of Object.entries(attributes)) {
    node.setAttribute(name === 'viewBox' ? name : name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`), value)
  }
  node.append(...children)
  return node
}

/** The start: a roof and walls in the 22 px tile, Board 7's own path. */
const house = (): SVGElement => svg('svg', {
  viewBox: '0 0 22 22', width: '22', height: '22', fill: 'none', stroke: 'currentColor', strokeWidth: '1.6',
}, svg('path', { d: 'M5 12l6-6l6 6M7 11v6h8v-6' }))

/** The new place: the green pin. */
const pin = (): SVGElement => svg('svg', { viewBox: '0 0 24 30', width: '22', height: '28', class: 'relative' },
  svg('path', {
    d: 'M12 29C3 17 1 12 1 8.5A11 11 0 1 1 23 8.5C23 12 21 17 12 29z',
    fill: 'currentColor', stroke: 'rgb(var(--color-surface))', strokeWidth: '2',
  }),
  svg('circle', { cx: '12', cy: '9', r: '4', fill: 'rgb(var(--color-surface))' }))

export function DayMap({ day, plan, gap, width, height }: {
  day: CalendarDay
  plan: DayPlan
  /** The gap the visit sits in, for where the place joins the route. */
  gap: number | null
  width: number | string
  height: number
}) {
  const host = useRef<HTMLDivElement>(null)
  const map = useRef<MapLibreMap | null>(null)
  const markers = useRef<Marker[]>([])
  const [session, setSession] = useState<ImagerySession | null | undefined>(undefined)
  const [ready, setReady] = useState(false)
  const [scale, setScale] = useState<{ label: string; pixels: number } | null>(null)

  useEffect(() => {
    let live = true
    imagerySession()
      .then((got) => { if (live) setSession(got) })
      .catch(() => { if (live) setSession(null) })
    return () => { live = false }
  }, [])

  /* The map, once the base is known; a plain one when there are no tiles. */
  useEffect(() => {
    if (session === undefined || !host.current || map.current) return undefined
    let cancelled = false
    let made: MapLibreMap | null = null
    void (async () => {
      const maplibre = await import('maplibre-gl')
      await import('maplibre-gl/dist/maplibre-gl.css')
      maplibre.setWorkerUrl((await import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url')).default)
      if (cancelled || !host.current) return
      made = new maplibre.Map({
        container: host.current,
        style: session
          ? {
            version: 8,
            sources: { base: { type: 'raster', tiles: session.tiles.map, tileSize: 256,
              maxzoom: Math.min(21, session.maxZoom) } },
            layers: [{ id: 'base', type: 'raster', source: 'base' }],
          }
          : {
            version: 8,
            sources: {},
            layers: [{ id: 'plain', type: 'background', paint: { 'background-color': tokenColour('surface-group') } }],
          },
        center: [-76.88, 40.27],
        zoom: 11,
        interactive: false,
        attributionControl: false,
        renderWorldCopies: false,
        pixelRatio: Math.min(2, window.devicePixelRatio || 1),
      })
      map.current = made
      made.on('load', () => { if (!cancelled) setReady(true) })
    })()
    return () => {
      cancelled = true
      for (const marker of markers.current) marker.remove()
      markers.current = []
      made?.remove()
      if (map.current === made) map.current = null
      setReady(false)
    }
  }, [session])

  /* The day: the route, the markers, and the camera fitted to them. */
  useEffect(() => {
    const current = map.current
    if (!ready || !current) return
    let cancelled = false
    void (async () => {
      const maplibre = await import('maplibre-gl')
      if (cancelled) return
      for (const marker of markers.current) marker.remove()
      markers.current = []

      const route = routeOf(day, gap)
      const line = {
        type: 'Feature' as const,
        properties: {},
        geometry: { type: 'LineString' as const, coordinates: route.map((one) => [one.lng, one.lat]) },
      }
      const source = current.getSource('route')
      if (source && 'setData' in source) {
        (source as { setData: (data: unknown) => void }).setData(line)
      } else {
        current.addSource('route', { type: 'geojson', data: line })
        current.addLayer({
          id: 'route', type: 'line', source: 'route',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': tokenColour('indigo'), 'line-opacity': 0.55, 'line-width': 3,
            'line-dasharray': [2, 1.7],
          },
        })
      }

      const add = (point: Point, element: HTMLElement, anchor: 'center' | 'bottom' | 'left' = 'center') => {
        markers.current.push(new maplibre.Marker({ element, anchor }).setLngLat([point.lng, point.lat]).addTo(current))
      }
      /*
       * The camera first, so each label can be put on the side with room. The
       * padding is what the markers need around their points: the pin stands
       * 28 px above its point with a 44 px halo, a stop is a 22 px circle, and
       * the scale sits in the bottom-right corner.
       */
      const points = route.length ? route : day.map.place ? [day.map.place] : []
      if (points.length > 0) {
        const bounds = new maplibre.LngLatBounds()
        for (const point of points) bounds.extend([point.lng, point.lat])
        current.fitBounds(bounds, { padding: { top: 44, bottom: 28, left: 26, right: 26 }, maxZoom: 15, duration: 0 })
      }
      const width = current.getContainer().clientWidth

      if (day.map.start) {
        add(day.map.start, markerElement(
          'flex h-[22px] w-[22px] items-center justify-center rounded-[6px] bg-toast text-toast-text', house()))
      }
      day.map.stops.forEach((stop, index) => {
        if (!stop.point) return
        const tone = milesTone(plan.stops[index]?.miles ?? null)
        /* A label that would run off the right-hand edge goes on the left of its circle. */
        const x = current.project([stop.point.lng, stop.point.lat]).x
        const left = x + 15 + stop.label.length * 6.2 > width - 4
        const element = markerElement(left ? 'flex flex-row-reverse items-center gap-1' : 'flex items-center gap-1')
        const circle = document.createElement('span')
        circle.className = 'flex h-[22px] w-[22px] items-center justify-center rounded-full border-[2.5px] bg-surface text-[11px] font-bold'
        circle.style.borderColor = TONE_VAR[tone]
        circle.style.color = TONE_VAR[tone]
        circle.textContent = String(index + 1)
        const label = document.createElement('span')
        label.className = 'whitespace-nowrap text-[11px] font-semibold text-text-body [text-shadow:0_0_3px_rgb(var(--color-surface))]'
        label.textContent = stop.label
        element.append(circle, label)
        markers.current.push(new maplibre.Marker({ element, anchor: left ? 'right' : 'left', offset: [left ? 11 : -11, 0] })
          .setLngLat([stop.point.lng, stop.point.lat]).addTo(current))
      })
      if (day.map.place) {
        const element = markerElement('relative flex text-accent', pin())
        const halo = document.createElement('span')
        halo.className = 'absolute left-1/2 top-[9px] h-11 w-11 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/[.12]'
        element.prepend(halo)
        add(day.map.place, element, 'bottom')
      }

      const centre = current.getCenter()
      setScale(scaleBar(metresPerPixel(centre.lat, current.getZoom()), 44))
    })()
    return () => { cancelled = true }
  }, [ready, day, plan, gap])

  return (
    <div className="relative overflow-hidden rounded-[12px] border border-line bg-surface-group" style={{ width, height }}
      data-day-map>
      {/* `h-full w-full` as well: MapLibre's stylesheet makes its container `position: relative`. */}
      <div ref={host} className="absolute inset-0 h-full w-full" />
      {scale && (
        <div className="absolute bottom-2 right-2.5 flex items-center gap-1.5 text-[10px] text-text-muted"
          aria-hidden>
          <span className="block h-[7px] border-x-2 border-b-2 border-text-muted" style={{ width: scale.pixels }} />
          {scale.label}
        </div>
      )}
      {session && (
        <div className="absolute bottom-0.5 left-1.5 max-w-[70%] truncate text-[8.5px] text-text-faint">
          {session.attribution}
        </div>
      )}
    </div>
  )
}
